import { Component, HostBinding, OnInit, computed, inject, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';

import { ButtonComponent } from '@shared/components/button/button.component';
import { ButtonSizeEnum } from '@shared/components/button/types/button-size.enum';
import { DialogRef } from '@shared/components/dialog';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputLabelComponent } from '@shared/components/input-label/input-label.component';
import { InputComponent } from '@shared/components/input/input.component';
import { SmallErrorComponent } from '@shared/components/small-error/small-error.component';
import { TextComponent } from '@shared/components/text';
import { ToastService, ToastType } from '@shared/components/toast/services/toast.service';
import { Disabled } from '@shared/directives/disabled/disabled.directive';
import { Crud, SpecialFormControl } from '@shared/services/crud/crud';
import { MeService } from '@shared/services/me/me.service';

interface ProfileForm {
  name: SpecialFormControl<string>;
  email: SpecialFormControl<string>;
  currentPassword: SpecialFormControl<string>;
  newPassword: SpecialFormControl<string>;
  confirmPassword: SpecialFormControl<string>;
}

function passwordsMatchValidator(): ValidatorFn {
  return (group: AbstractControl) => {
    const pw = group.get('newPassword')?.value as string;
    const confirm = group.get('confirmPassword')?.value as string;
    if (pw && confirm && pw !== confirm) {
      return { passwordsMismatch: true };
    }
    return null;
  };
}

@Component({
  selector: 'l-profile-dialog',
  standalone: true,
  imports: [ButtonComponent, InputComponent, InputLabelComponent, SmallErrorComponent, IconComponent, TextComponent, Disabled, ReactiveFormsModule],
  templateUrl: './profile-dialog.component.html'
})
export class ProfileDialogComponent extends Crud<ProfileForm> implements OnInit {
  @HostBinding('class')
  public get class() {
    return 'flex h-full min-h-[100vh] max-h-[100dvh] w-3/5 min-w-[500px] max-w-[600px] ' + 'flex-col overflow-hidden rounded-l-xl bg-basic-100';
  }

  protected readonly ButtonSizeEnum = ButtonSizeEnum;

  protected readonly showPasswordSection = signal(false);

  private readonly meService = inject(MeService);
  private readonly toastService = inject(ToastService);
  private readonly dialogRef = inject(DialogRef, { optional: true });

  protected readonly me = this.meService.me;

  protected readonly avatarInitial = computed(() => {
    const name = this.me()?.name ?? '';
    return name.trim().charAt(0).toUpperCase() || '?';
  });

  public override form = new FormGroup<ProfileForm>({
    name: new SpecialFormControl(
      '',
      {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2), Validators.maxLength(150)]
      },
      null,
      {
        required: 'Nome é obrigatório',
        minlength: 'Mínimo de 2 caracteres',
        maxlength: 'No máximo 150 caracteres'
      }
    ),
    email: new SpecialFormControl(
      '',
      {
        nonNullable: true,
        validators: [Validators.required, Validators.email]
      },
      null,
      {
        required: 'E-mail é obrigatório',
        email: 'E-mail inválido'
      }
    ),
    currentPassword: new SpecialFormControl('', { nonNullable: true }, null, {
      required: 'Senha atual é obrigatória para alterar a senha',
      minlength: 'Mínimo de 6 caracteres'
    }),
    newPassword: new SpecialFormControl('', { nonNullable: true }, null, {
      required: 'Nova senha é obrigatória',
      minlength: 'Mínimo de 6 caracteres'
    }),
    confirmPassword: new SpecialFormControl('', { nonNullable: true }, null, {
      required: 'Confirmação é obrigatória'
    })
  });

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();

    const user = this.me();
    if (user) {
      this.form.patchValue({ name: user.name, email: user.email });
    }
  }

  protected togglePasswordSection(): void {
    const next = !this.showPasswordSection();
    this.showPasswordSection.set(next);

    const { currentPassword, newPassword, confirmPassword } = this.form.controls;

    if (next) {
      currentPassword.setValidators([Validators.required, Validators.minLength(6)]);
      newPassword.setValidators([Validators.required, Validators.minLength(6)]);
      confirmPassword.setValidators([Validators.required]);
      this.form.setValidators(passwordsMatchValidator());
    } else {
      currentPassword.clearValidators();
      newPassword.clearValidators();
      confirmPassword.clearValidators();
      this.form.clearValidators();
      this.form.patchValue({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }

    currentPassword.updateValueAndValidity();
    newPassword.updateValueAndValidity();
    confirmPassword.updateValueAndValidity();
    this.form.updateValueAndValidity();
    this.tap();
  }

  public close(): void {
    this.dialogRef ? this.dialogRef.close() : this.return();
  }

  public async submit(): Promise<void> {
    if (!this.guardSubmit()) {
      this.tap();
      return;
    }

    this.isSaving.set(true);

    const { name, email, currentPassword, newPassword } = this.form.getRawValue();

    const result = await this.meService.patch({
      name,
      email,
      ...(this.showPasswordSection() ? { currentPassword, password: newPassword } : {})
    });

    this.isSaving.set(false);

    if (result.isLeft()) {
      this.toastService.show({
        title: result.value.error?.message ?? 'Erro ao salvar perfil',
        type: ToastType.ERROR
      });
      return;
    }

    this.toastService.show({ title: 'Perfil atualizado com sucesso', type: ToastType.SUCCESS });
    this.close();
  }
}
